import React, { useEffect, useRef, useState } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CollectionsRoundedIcon from '@mui/icons-material/CollectionsRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { SchoolEvent } from 'models/event';
import { getSchoolEventPhotos, getSchoolEventVideos } from 'services/school';
import Loader from 'components/loader';
import './EventGallery.scss';

interface Props { event: SchoolEvent; activeSchoolId: string; }
interface MediaItem { id: string; type: 'photo' | 'video'; url: string; thumbnail?: string; name: string; }

const getVideoUrl = (video: any) => {
   const streams = Array.isArray(video.streamData) ? video.streamData : [];
   return streams.find((stream: any) => stream.quality === 'HIGH')?.url || streams[0]?.url || video.picUrl || '';
};

export default function EventGallery({ event, activeSchoolId }: Props) {
   const closeButtonRef = useRef<HTMLButtonElement>(null);
   const [items, setItems] = useState<MediaItem[]>([]);
   const [selected, setSelected] = useState<MediaItem>();
   const [isLoading, setIsLoading] = useState(true);
   const [loadError, setLoadError] = useState(false);

   useEffect(() => {
      let active = true;
      setIsLoading(true);
      Promise.all([getSchoolEventPhotos(activeSchoolId, event.id), getSchoolEventVideos(activeSchoolId, event.id)])
         .then(([photos, videos]) => {
            if (!active) return;
            const photoItems = (Array.isArray(photos) ? photos : []).filter(photo => photo.picUrl).map(photo => ({ id: photo.id, type: 'photo' as const, url: photo.picUrl, thumbnail: photo.picUrl, name: photo.description?.trim() || 'Event photo', caption: photo.description?.trim() || '' }));
            const videoItems = (Array.isArray(videos) ? videos : []).map(video => ({ id: video.id, type: 'video' as const, url: getVideoUrl(video), thumbnail: video.picUrl, name: video.fileName || 'Event video', caption: video.fileName || '' })).filter(video => video.url);
            setItems([...photoItems, ...videoItems]);
         })
         .catch(() => { if (active) setLoadError(true); })
         .finally(() => { if (active) setIsLoading(false); });
      return () => { active = false; };
   }, [activeSchoolId, event.id]);

   useEffect(() => {
      if (!selected) return;
      const previousOverflow = document.body.style.overflow;
      const close = (keyboardEvent: KeyboardEvent) => { if (keyboardEvent.key === 'Escape') setSelected(undefined); };
      document.body.style.overflow = 'hidden';
      closeButtonRef.current?.focus();
      document.addEventListener('keydown', close);
      return () => {
         document.body.style.overflow = previousOverflow;
         document.removeEventListener('keydown', close);
      };
   }, [selected]);

   if (isLoading) return <div className='event-gallery__loading'><Loader /></div>;
   if (loadError) return <section className='event-gallery__empty' role='alert'><CollectionsRoundedIcon /><h2>Gallery could not be loaded</h2><p>Please refresh the page and try again.</p></section>;
   if (items.length === 0) return <section className='event-gallery__empty'><CollectionsRoundedIcon /><h2>No photos or videos yet</h2><p>Media from this event will appear here when the school publishes it.</p></section>;

   return <div className='event-gallery'>
      <div className='event-gallery__heading'><div><h2>Photos &amp; video</h2><p>Highlights published by the school.</p></div><span>{items.length} {items.length === 1 ? 'item' : 'items'}</span></div>
      <div className='event-gallery__grid'>{items.map(item => <button key={`${item.type}-${item.id}`} type='button' onClick={() => setSelected(item)}>
         {item.type === 'photo' ? <img src={item.thumbnail} alt={item.name} loading='lazy' /> : item.thumbnail ? <img src={item.thumbnail} alt='' loading='lazy' /> : <video src={item.url} preload='metadata' />}
         {item.type === 'video' && <i><PlayArrowRoundedIcon /></i>}
         {item.caption && <span>{item.caption}</span>}
      </button>)}</div>
      {selected && <div className='event-gallery__viewer' role='dialog' aria-modal='true' aria-label={selected.name} onMouseDown={event => { if (event.target === event.currentTarget) setSelected(undefined); }}>
         <button ref={closeButtonRef} type='button' onClick={() => setSelected(undefined)} aria-label='Close media viewer'><CloseRoundedIcon /></button>
         <figure>{selected.type === 'photo' ? <img src={selected.url} alt={selected.name} /> : <video src={selected.url} controls autoPlay />}{selected.caption && <figcaption>{selected.caption}</figcaption>}</figure>
      </div>}
   </div>;
}
